'use client'

import type {
  IntegrationFormSettings,
  IntegrationProvider,
} from '@/lib/integrations/catalog'

export type ProviderSettings = IntegrationFormSettings

export interface ProviderFormConfig {
  isValid: (settings: ProviderSettings) => boolean
  getDefaultSettings: () => Partial<ProviderSettings>
}

const PROVIDER_CONFIGS: Partial<
  Record<IntegrationProvider, ProviderFormConfig>
> = {
  braze: {
    isValid: (settings) =>
      Boolean(settings.apiKey.trim() && settings.instance?.trim()),
    getDefaultSettings: () => ({ apiKey: '', instance: '' }),
  },
  brevo: {
    isValid: (settings) => Boolean(settings.apiKey.trim()),
    getDefaultSettings: () => ({ apiKey: '' }),
  },
  clerk: {
    // Clerk: the "API key" is the webhook signing secret. Format check
    // only (no external endpoint to validate against).
    isValid: (settings) => {
      const trimmed = settings.apiKey.trim()
      return trimmed.startsWith('whsec_') && trimmed.length >= 24
    },
    getDefaultSettings: () => ({
      apiKey: '',
      webhookUrl: '',
      connectionId: '',
    }),
  },
  customerio: {
    isValid: (settings) => Boolean(settings.apiKey.trim()),
    getDefaultSettings: () => ({ apiKey: '', region: 'us' }),
  },
  mailjet: {
    isValid: (settings) =>
      Boolean(settings.apiKey.trim() && settings.secretKey?.trim()),
    getDefaultSettings: () => ({ apiKey: '', secretKey: '' }),
  },
  iterable: {
    isValid: (settings) => Boolean(settings.apiKey.trim()),
    getDefaultSettings: () => ({ apiKey: '', region: 'us' }),
  },
  postmark: {
    isValid: (settings) => Boolean(settings.apiKey.trim()),
    getDefaultSettings: () => ({ apiKey: '' }),
  },
  onesignal: {
    isValid: (settings) =>
      Boolean(settings.apiKey.trim() && settings.appId?.trim()),
    getDefaultSettings: () => ({ apiKey: '', appId: '' }),
  },
  mailgun: {
    isValid: (settings) =>
      Boolean(settings.apiKey.trim() && settings.domain?.trim()),
    getDefaultSettings: () => ({ apiKey: '', domain: '' }),
  },
  sendgrid: {
    isValid: (settings) => Boolean(settings.apiKey.trim()),
    getDefaultSettings: () => ({ apiKey: '' }),
  },
  stytch: {
    isValid: (settings) => {
      const trimmed = settings.apiKey.trim()
      return trimmed.startsWith('whsec_') && trimmed.length >= 24
    },
    getDefaultSettings: () => ({
      apiKey: '',
      webhookUrl: '',
      connectionId: '',
    }),
  },
  supabase: {
    isValid: (settings) => settings.apiKey.trim().length >= 16,
    getDefaultSettings: () => ({
      apiKey: '',
      webhookUrl: '',
      connectionId: '',
    }),
  },
  workos: {
    isValid: (settings) => settings.apiKey.trim().length >= 16,
    getDefaultSettings: () => ({
      apiKey: '',
      webhookUrl: '',
      connectionId: '',
    }),
  },
  shopify: {
    isValid: (settings) =>
      settings.apiKey.trim().length >= 16 && Boolean(settings.domain?.trim()),
    getDefaultSettings: () => ({
      apiKey: '',
      domain: '',
      webhookUrl: '',
      connectionId: '',
    }),
  },
  revenuecat: {
    isValid: (settings) => settings.apiKey.trim().length >= 16,
    getDefaultSettings: () => ({
      apiKey: '',
      webhookUrl: '',
      connectionId: '',
      revenueCatVerificationMode: 'hmac',
    }),
  },
}

export function getProviderConfig(
  provider: IntegrationProvider
): ProviderFormConfig | undefined {
  return PROVIDER_CONFIGS[provider]
}
